import { createContext, useContext, useState, useEffect } from 'react';
import { getMe, updateTheme as apiUpdateTheme } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      getMe()
        .then((res) => {
          setUser(res.data);
          applyTheme(res.data.theme, res.data.accent_color, res.data.font_theme);
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        })
        .finally(() => setLoading(false));
    } else {
      // Default to dark theme with cyan accent
      applyTheme('dark', 'cyan', 'modern');
      setLoading(false);
    }
  }, []);

  const applyTheme = (theme, accentColor, fontTheme) => {
    const root = document.documentElement;
    
    // Apply dark/light mode
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // Apply accent color
    const accentClasses = ['accent-cyan', 'accent-lime', 'accent-pink', 'accent-orange', 'accent-violet', 'accent-emerald', 'accent-red', 'accent-gold', 'accent-blue', 'accent-green', 'accent-purple'];
    accentClasses.forEach(cls => root.classList.remove(cls));
    root.classList.add(`accent-${accentColor || 'cyan'}`);
    
    // Apply font theme
    const fontClasses = ['font-modern', 'font-mono', 'font-elegant', 'font-bold', 'font-tech'];
    fontClasses.forEach(cls => root.classList.remove(cls));
    root.classList.add(`font-${fontTheme || 'modern'}`);
  };

  const login = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    applyTheme(userData.theme || 'dark', userData.accent_color || 'cyan', userData.font_theme || 'modern');
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateTheme = async (theme, accentColor, fontTheme) => {
    await apiUpdateTheme(theme, accentColor, fontTheme);
    setUser({ ...user, theme, accent_color: accentColor, font_theme: fontTheme });
    applyTheme(theme, accentColor, fontTheme);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, updateTheme }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
